import { Button } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { clearUserIdentityCache } from 'shared/administration/api/identity'
import { TextV2 } from 'shared/components/Text/TextV2'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { reloadOrganization } from '../helpers/switchOrganization'
import { LOGOUT_USER } from '../redux/actions/auth/authActionTypes'
import { freezeOrganizationPersistence } from '../redux/store/organizationPersistence'
import { persistor } from '../redux/store/persistor'
import { store } from '../redux/store/store'

export const OrganizationSessionBoundary = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<'ready' | 'changed' | 'switching' | 'failed'>('ready')
  const queryClient = useQueryClient()
  useEffect(() => {
    const onChanged = () => {
      setState('changed')
      // Clear only this document. Its logout state must not overwrite the new tab's shared sign-in.
      freezeOrganizationPersistence()
      persistor.pause()
      clearUserIdentityCache()
      store.dispatch({ type: LOGOUT_USER })
      queryClient.clear()
    }
    const onSwitching = () => setState('switching')
    const onFailed = () => setState('failed')
    window.addEventListener('organization-changed', onChanged)
    window.addEventListener('organization-switching', onSwitching)
    window.addEventListener('organization-switch-failed', onFailed)
    return () => {
      window.removeEventListener('organization-changed', onChanged)
      window.removeEventListener('organization-switching', onSwitching)
      window.removeEventListener('organization-switch-failed', onFailed)
    }
  }, [queryClient])
  if (state === 'ready') return <>{children}</>
  if (state === 'switching')
    return (
      <main className="m-8" role="status">
        <TextV2 type={TYPOGRAPHY.header4}>Switching organization…</TextV2>
      </main>
    )
  return (
    <main className="m-8" role="alert">
      <TextV2 type={TYPOGRAPHY.header4}>
        {state === 'failed'
          ? 'Unable to finish switching organizations'
          : 'Your organization changed in another tab'}
      </TextV2>
      <TextV2 type={TYPOGRAPHY.paragraph1}>Reload to continue with its data and permissions.</TextV2>
      <Button onClick={() => reloadOrganization(queryClient)}>Reload app</Button>
    </main>
  )
}
